/** Markdown-ul modelului, cât încape într-un panou de 400 px.
 *
 *  DE CE E SCRIS AICI ȘI NU LUAT DE-A GATA. O bibliotecă de markdown aduce un
 *  parser întreg plus un dezinfectant de HTML, pentru un panou care are nevoie
 *  de cinci lucruri: îngroșat, cursiv, cod, liste, titluri. Iar partea care
 *  contează nu e parsarea, e că textul vine de la un model care tocmai a citit
 *  un document al cuiva: nimic de aici nu ajunge în `dangerouslySetInnerHTML`,
 *  se compun NODURI React, deci un `<img onerror=…>` scris în răspuns rămâne
 *  exact ce e — text.
 *
 *  CE ÎNȚELEGE: `# ` … `### ` titluri, `- ` și `1. ` liste, ``` blocuri de cod,
 *  `**îngroșat**`, `*cursiv*`, `` `cod` ``. Ce nu înțelege trece ca text, nu ca
 *  eroare — un răspuns cu un tabel se citește prost, dar se citește.
 */
import type { ReactNode } from "react";

/** O bucată de text, cu marcajele ei inline, ca noduri React. */
export function inline(text: string): ReactNode[] {
  // O singură trecere, cu un tipar alternativ: `cod` bate îngroșatul, ca un
  // `**` scris într-un fragment de cod să rămână acolo unde e.
  const tipar = /`([^`]+)`|\*\*([^*]+)\*\*|\*([^*\n]+)\*/g;
  const iesire: ReactNode[] = [];
  let ultim = 0;
  let m: RegExpExecArray | null;
  let k = 0;
  while ((m = tipar.exec(text))) {
    if (m.index > ultim) iesire.push(text.slice(ultim, m.index));
    if (m[1] !== undefined) iesire.push(<code key={k++}>{m[1]}</code>);
    else if (m[2] !== undefined) iesire.push(<strong key={k++}>{m[2]}</strong>);
    else iesire.push(<em key={k++}>{m[3]}</em>);
    ultim = m.index + m[0].length;
  }
  if (ultim < text.length) iesire.push(text.slice(ultim));
  return iesire;
}

type Bloc =
  | { fel: "titlu"; nivel: 1 | 2 | 3; text: string }
  | { fel: "lista"; numerotata: boolean; start?: number; randuri: string[] }
  | { fel: "cod"; text: string }
  | { fel: "paragraf"; text: string };

/** Un punct de listă: `- `, `* `, `1. ` sau `1) `, cu indentarea lui. */
const PUNCT = /^\s*([-*]|\d+[.)])\s+(.*)$/;

/** Textul, tăiat în blocuri. Exportat ca să se poată proba fără React. */
export function blocuri(text: string): Bloc[] {
  const randuri = text.split("\n");
  const iesire: Bloc[] = [];
  let i = 0;
  while (i < randuri.length) {
    const r = randuri[i];

    if (r.startsWith("```")) {
      const strans: string[] = [];
      i++;
      while (i < randuri.length && !randuri[i].startsWith("```")) {
        strans.push(randuri[i]);
        i++;
      }
      i++;                                    // rândul de închidere
      iesire.push({ fel: "cod", text: strans.join("\n") });
      continue;
    }

    const titlu = /^(#{1,3})\s+(.*)$/.exec(r);
    if (titlu) {
      iesire.push({ fel: "titlu", nivel: titlu[1].length as 1 | 2 | 3,
                    text: titlu[2] });
      i++;
      continue;
    }

    const punct = PUNCT.exec(r);
    if (punct) {
      const numerotata = !/^[-*]$/.test(punct[1]);
      const deFel = (p: RegExpExecArray | null) => !!p && (!/^[-*]$/.test(p[1])) === numerotata;
      const strans: string[] = [];
      while (i < randuri.length) {
        const p = PUNCT.exec(randuri[i]);
        if (p) {
          if (!deFel(p)) break;
          strans.push(p[2]);
          i++;
          continue;
        }
        // rândul indentat de sub un punct e tot al lui
        if (/^\s{2,}\S/.test(randuri[i])) {
          strans[strans.length - 1] += " " + randuri[i].trim();
          i++;
          continue;
        }
        // RÂNDURILE GOALE DINTRE PUNCTE NU RUP LISTA. Modelul lasă adesea câte
        // unul între puncte; rupt acolo, fiecare punct era o listă a lui,
        // numerotată iar de la 1
        let j = i;
        while (j < randuri.length && !randuri[j].trim()) j++;
        if (j > i && j < randuri.length && deFel(PUNCT.exec(randuri[j]))) {
          i = j;
          continue;
        }
        break;
      }
      // o listă reluată după un paragraf continuă numărătoarea ei
      const start = numerotata ? parseInt(punct[1], 10) : undefined;
      iesire.push({ fel: "lista", numerotata, ...(start && start !== 1 ? { start } : {}), randuri: strans });
      continue;
    }

    if (!r.trim()) { i++; continue; }

    // Un paragraf ține până la rândul gol, la titlu sau la listă. Rândurile
    // rupte în mijlocul frazei se lipesc — așa se scrie markdown.
    const strans: string[] = [];
    while (i < randuri.length) {
      const p = randuri[i];
      if (!p.trim() || p.startsWith("```") || /^(#{1,3})\s/.test(p)
          || PUNCT.test(p)) break;
      strans.push(p);
      i++;
    }
    iesire.push({ fel: "paragraf", text: strans.join("\n") });
  }
  return iesire;
}

export default function Scris({ text }: { text: string }) {
  return (
    <div className="ob-as-scris-text">
      {blocuri(text).map((b, i) => {
        if (b.fel === "cod") return <pre key={i} className="ob-as-cod">{b.text}</pre>;
        if (b.fel === "titlu") {
          const H = (["h4", "h5", "h6"] as const)[b.nivel - 1];
          return <H key={i} className={`ob-as-t${b.nivel}`}>{inline(b.text)}</H>;
        }
        if (b.fel === "lista") {
          const L = b.numerotata ? "ol" : "ul";
          return (
            <L key={i} className="ob-as-lista" start={b.numerotata ? b.start : undefined}>
              {b.randuri.map((r, j) => <li key={j}>{inline(r)}</li>)}
            </L>
          );
        }
        return <p key={i} className="ob-as-p">{inline(b.text)}</p>;
      })}
    </div>
  );
}
